import { useState } from 'react';
import type { RrcCompanyProfile } from '@ots-frontend/ots-data';
import {
  OtsForm,
  OtsFormActions,
  OtsFormColumns,
  OtsFormErrorSummary,
  OtsFormRow,
  OtsFormSection,
} from '../../components/OtsForm';
import { OtsTextInput } from '../../components/OtsTextInput';
import { OtsSelect, type OtsSelectOption } from '../../components/OtsSelect';
import { OtsSwitch } from '../../components/OtsSwitch';
import { OtsDateTimePicker } from '../../components/OtsDateTimePicker';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import {
  OtsCompanySelect,
  OtsPersonSelect,
} from '../../select/OtsEntitySelects';
import {
  companyProfileOptions,
  toCompanyProfileDraft,
  toCompanyProfilePayload,
  validateCompanyProfile,
  type CompanyProfileDraft,
  type CompanyProfileErrors,
} from './rrc-company-profile-ui';

export type RrcCompanyProfileFormProps = {
  profile?: Partial<RrcCompanyProfile>;
  mode?: 'create' | 'edit' | 'copy';
  companyLocked?: boolean;
  regionId?: string;
  companyOptions?: OtsSelectOption[];
  personOptions?: OtsSelectOption[];
  compact?: boolean;
  submitting?: boolean;
  error?: string;
  onSubmit: (profile: Partial<RrcCompanyProfile>) => void;
  onCancel?: () => void;
};

export const RrcCompanyProfileForm = ({
  profile,
  mode = 'create',
  companyLocked,
  regionId,
  companyOptions,
  personOptions,
  compact,
  submitting,
  error,
  onSubmit,
  onCancel,
}: RrcCompanyProfileFormProps) => {
  const [draft, setDraft] = useState(() => toCompanyProfileDraft(profile));
  const [errors, setErrors] = useState<CompanyProfileErrors>({});
  const [attempted, setAttempted] = useState(false);
  const size = compact ? 'compact' : 'default';
  const options = companyProfileOptions();
  const change = (patch: Partial<CompanyProfileDraft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (attempted) setErrors(validateCompanyProfile(next));
  };
  const submit = () => {
    if (submitting) return;
    const next = validateCompanyProfile(draft);
    setErrors(next);
    setAttempted(true);
    if (!Object.keys(next).length)
      onSubmit(
        toCompanyProfilePayload(
          draft,
          mode === 'edit' ? profile?.id : undefined
        )
      );
  };
  const input = (
    key:
      | 'orgName'
      | 'p5Number'
      | 'permitNumber'
      | 'contactName'
      | 'contactPhone'
      | 'contactEmail',
    label: string,
    required = false
  ) => (
    <OtsTextInput
      label={label}
      required={required}
      value={draft[key]}
      error={errors[key]}
      size={size}
      onChange={(event) => change({ [key]: event.currentTarget.value })}
      type={key === 'contactEmail' ? 'email' : 'text'}
    />
  );
  return (
    <OtsForm
      aria-label={
        mode === 'edit'
          ? 'Edit company profile'
          : mode === 'copy'
          ? 'Copy company profile'
          : 'New company profile'
      }
      noValidate
      density={size}
      disabled={submitting}
      onSubmit={submit}
      alert={
        <>
          {error && (
            <OtsInlineAlert tone="danger" title="Profile could not be saved">
              {error}
            </OtsInlineAlert>
          )}
          {attempted && <OtsFormErrorSummary errors={errors} />}
        </>
      }
      actions={
        <OtsFormActions
          onCancel={onCancel}
          submitting={submitting}
          submitLabel={
            mode === 'edit'
              ? 'Save changes'
              : mode === 'copy'
              ? 'Create version'
              : 'Create profile'
          }
          submittingLabel="Saving…"
        />
      }
    >
      <OtsFormColumns>
        <div className="space-y-4">
          <OtsFormSection
            title="Company & registration"
            description="The company, role, and district identify a profile series and cannot change during an edit."
          >
            <OtsCompanySelect
              label="Company"
              selectedOptions={
                profile?.companyId
                  ? [
                      {
                        value: profile.companyId,
                        label: profile.companyName ?? profile.companyId,
                      },
                    ]
                  : undefined
              }
              value={draft.companyId}
              onChange={(companyId) => change({ companyId: companyId ?? '' })}
              disabled={mode === 'edit' || companyLocked}
              regionId={regionId}
              size={size}
              options={
                companyOptions ??
                ((mode === 'edit' || companyLocked) && profile?.companyId
                  ? [
                      {
                        value: profile.companyId,
                        label: profile.companyName ?? profile.companyId,
                      },
                    ]
                  : undefined)
              }
            />
            {errors.companyId && (
              <p role="alert" className="text-xs text-[var(--ots-error)]">
                Company: {errors.companyId}
              </p>
            )}
            <OtsFormRow>
              <OtsSelect
                label="RRC district"
                required
                options={options.districts}
                value={draft.rrcDistrict}
                disabled={mode === 'edit'}
                onChange={(rrcDistrict) =>
                  change({ rrcDistrict: rrcDistrict ?? '' })
                }
                error={errors.rrcDistrict}
                size={size}
                placeholder="Select a district"
              />
              <OtsSelect
                label="Role"
                required
                options={options.roles}
                value={draft.role}
                disabled={mode === 'edit'}
                onChange={(role) => change({ role: role ?? '' })}
                error={errors.role}
                size={size}
                placeholder="Select a role"
              />
            </OtsFormRow>
            {input('orgName', 'Organization name', true)}
            <OtsFormRow>
              {input('p5Number', 'P5 number')}
              {input('permitNumber', 'Permit number')}
            </OtsFormRow>
            <OtsSwitch
              label="Commercial"
              checked={draft.commercial}
              onCheckedChange={(commercial) => change({ commercial })}
              size={size}
            />
          </OtsFormSection>
          <OtsFormSection
            title="Effective dates"
            description="The server maintains version history and checks for overlapping effective periods."
          >
            <OtsFormRow>
              <OtsDateTimePicker
                label="Effective start"
                required
                value={draft.start}
                onChange={(start) => change({ start })}
                error={errors.start}
                size={size}
              />
              <OtsDateTimePicker
                label="Effective end"
                value={draft.end}
                onChange={(end) => change({ end })}
                error={errors.end}
                size={size}
                description="Leave blank for no end date."
              />
            </OtsFormRow>
            {mode !== 'create' && (
              <OtsInlineAlert title="Version history is preserved" size={size}>
                Saving an effective profile may create a replacement version.
                Existing manifests keep their recorded profile snapshot.
              </OtsInlineAlert>
            )}
          </OtsFormSection>
          <OtsFormSection
            title="Automatic certification"
            description="Configure EP-9 and EP-10 signing independently."
          >
            {(['autoEp9', 'autoEp10'] as const).map((form) => (
              <OtsFormRow key={form}>
                <OtsSelect
                  label={`${
                    form === 'autoEp9' ? 'EP-9' : 'EP-10'
                  } signer strategy`}
                  value={draft[`${form}SignerStrategy`]}
                  options={options.strategies}
                  placeholder="Not configured"
                  size={size}
                  onChange={(strategy) =>
                    change({ [`${form}SignerStrategy`]: strategy ?? '' })
                  }
                />
                <div>
                  <OtsPersonSelect
                    label={`${form === 'autoEp9' ? 'EP-9' : 'EP-10'} signer`}
                    selectedOptions={
                      profile?.[`${form}SignerId`]
                        ? [
                            {
                              value: profile[`${form}SignerId`]!,
                              label:
                                profile[`${form}SignerName`] ??
                                profile[`${form}SignerId`]!,
                            },
                          ]
                        : undefined
                    }
                    regionId={regionId}
                    options={personOptions}
                    value={draft[`${form}SignerId`]}
                    onChange={(id) => change({ [`${form}SignerId`]: id ?? '' })}
                    disabled={draft[`${form}SignerStrategy`] !== 'SELECTED'}
                    size={size}
                  />
                  {errors[`${form}SignerId`] && (
                    <p
                      role="alert"
                      className="mt-1 text-xs text-[var(--ots-error)]"
                    >
                      {errors[`${form}SignerId`]}
                    </p>
                  )}
                </div>
              </OtsFormRow>
            ))}
          </OtsFormSection>
        </div>
        <div className="space-y-4">
          <OtsFormSection title="Contact">
            {input('contactName', 'Contact name')}
            <OtsFormRow>
              {input('contactPhone', 'Contact phone')}
              {input('contactEmail', 'Contact email')}
            </OtsFormRow>
          </OtsFormSection>
          <OtsFormSection title="Address">
            {(
              [
                'street',
                'street2',
                'city',
                'county',
                'state',
                'province',
                'postalCode',
                'country',
              ] as const
            ).map((key) => (
              <OtsTextInput
                key={key}
                label={
                  {
                    street: 'Street',
                    street2: 'Address line 2',
                    city: 'City',
                    county: 'County',
                    state: 'State',
                    province: 'Province',
                    postalCode: 'Postal code',
                    country: 'Country',
                  }[key]
                }
                value={draft.address[key] ?? ''}
                onChange={(event) =>
                  change({
                    address: {
                      ...draft.address,
                      [key]: event.currentTarget.value,
                    },
                  })
                }
                size={size}
              />
            ))}
          </OtsFormSection>
        </div>
      </OtsFormColumns>
    </OtsForm>
  );
};
